// Lista Global de Pendências - Bloco 4
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Checkbox } from '@/components/ui/checkbox';
import { 
  Search, Filter, ChevronLeft, ChevronRight, ArrowUpDown,
  AlertTriangle, Clock, CheckCircle, Loader2, Eye
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = window.location.origin;

const STATUS_CONFIG = {
  lancado: { label: 'Lançado', color: 'bg-green-100 text-green-700', icon: CheckCircle },
  pendente: { label: 'Pendente', color: 'bg-yellow-100 text-yellow-700', icon: Clock },
  parcial: { label: 'Parcial', color: 'bg-blue-100 text-blue-700', icon: Clock },
  atrasado: { label: 'Atrasado', color: 'bg-orange-100 text-orange-700', icon: Clock },
  critico: { label: 'Crítico', color: 'bg-red-100 text-red-700', icon: AlertTriangle }
};

export function ListaPendencias({ year, trimestre, filtros }) {
  const [data, setData] = useState({ pendencias: [], total: 0, estatisticas: {} });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [limit] = useState(20);
  const [auditoria, setAuditoria] = useState(null);
  const [auditLoading, setAuditLoading] = useState(false);

  const verMotivo = async (p) => {
    setAuditLoading(true);
    setAuditoria({ ...p, _loading: true });
    try {
      const params = new URLSearchParams({
        year: String(year), trimestre,
        turma_id: p.turma_id || '',
        tipo_avaliacao: p.tipo_avaliacao || '',
      });
      if (p.disciplina_id) params.set('disciplina_id', p.disciplina_id);
      if (p.professor_id) params.set('teacher_id', p.professor_id);
      const r = await fetch(`${API_URL}/api/pendencias-academicas/auditoria?${params.toString()}`);
      if (!r.ok) throw new Error();
      const d = await r.json();
      setAuditoria(d);
    } catch {
      toast.error('Falha ao carregar auditoria');
      setAuditoria(null);
    } finally { setAuditLoading(false); }
  };
  const [sortBy, setSortBy] = useState('status');
  const [sortOrder, setSortOrder] = useState('asc');
  
  // Filtros locais
  const [turmaId, setTurmaId] = useState('');
  const [disciplinaId, setDisciplinaId] = useState('');
  const [tipoAvaliacao, setTipoAvaliacao] = useState('');
  const [status, setStatus] = useState('');
  const [apenasCriticas, setApenasCriticas] = useState(false);

  useEffect(() => {
    loadData();
  }, [year, trimestre, page, search, turmaId, disciplinaId, tipoAvaliacao, status, apenasCriticas, sortBy, sortOrder]);

  const loadData = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        year: year.toString(),
        trimestre,
        page: page.toString(),
        limit: limit.toString(),
        sort_by: sortBy,
        sort_order: sortOrder
      });
      
      if (search) params.append('search', search);
      if (turmaId) params.append('class_group_id', turmaId);
      if (disciplinaId) params.append('subject_id', disciplinaId);
      if (tipoAvaliacao) params.append('tipo_avaliacao', tipoAvaliacao);
      if (status) params.append('status', status);
      if (apenasCriticas) params.append('apenas_criticas', 'true');
      
      const res = await fetch(`${API_URL}/api/pendencias-academicas/lista?${params}`);
      const json = await res.json();
      setData(json);
    } catch (error) {
      console.error('Erro ao carregar lista:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('asc');
    }
    setPage(1);
  };

  const clearFilters = () => {
    setSearch('');
    setTurmaId('');
    setDisciplinaId('');
    setTipoAvaliacao('');
    setStatus('');
    setApenasCriticas(false);
    setPage(1);
  };

  const filtrosAtivos = [turmaId, disciplinaId, tipoAvaliacao, status, apenasCriticas ? 'criticas' : ''].filter(Boolean).length;

  return (
    <div className="space-y-4" data-testid="lista-pendencias">
      {/* Filtros */}
      <Card className="border-purple-100">
        <CardContent className="pt-4">
          <div className="flex items-center gap-3 flex-wrap">
            {/* Busca */}
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Buscar turma, disciplina, professor..."
                value={search}
                onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                className="pl-10 bg-slate-50 border-slate-200"
              />
            </div>
            
            {/* Turma */}
            <Select value={turmaId || "all"} onValueChange={(v) => { setTurmaId(v === "all" ? "" : v); setPage(1); }}>
              <SelectTrigger className="w-[160px] bg-slate-50 border-slate-200">
                <SelectValue placeholder="Turma" />
              </SelectTrigger>
              <SelectContent className="bg-white max-h-[300px]">
                <SelectItem value="all">Todas</SelectItem>
                {(filtros?.turmas || []).map((t) => (
                  <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            {/* Disciplina */}
            <Select value={disciplinaId || "all"} onValueChange={(v) => { setDisciplinaId(v === "all" ? "" : v); setPage(1); }}>
              <SelectTrigger className="w-[140px] bg-slate-50 border-slate-200">
                <SelectValue placeholder="Disciplina" />
              </SelectTrigger>
              <SelectContent className="bg-white">
                <SelectItem value="all">Todas</SelectItem>
                {(filtros?.disciplinas || []).map((d) => (
                  <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            {/* Tipo */}
            <Select value={tipoAvaliacao || "all"} onValueChange={(v) => { setTipoAvaliacao(v === "all" ? "" : v); setPage(1); }}>
              <SelectTrigger className="w-[160px] bg-slate-50 border-slate-200">
                <SelectValue placeholder="Tipo" />
              </SelectTrigger>
              <SelectContent className="bg-white">
                <SelectItem value="all">Todos</SelectItem>
                {(filtros?.tipos_avaliacao || []).map((t) => (
                  <SelectItem key={t} value={t}>{t}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            {/* Status */}
            <Select value={status || "all"} onValueChange={(v) => { setStatus(v === "all" ? "" : v); setPage(1); }}>
              <SelectTrigger className="w-[130px] bg-slate-50 border-slate-200">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent className="bg-white">
                <SelectItem value="all">Todos</SelectItem>
                {(filtros?.status || []).map((s) => (
                  <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            {/* Apenas Críticas */}
            <div className="flex items-center gap-2">
              <Checkbox
                id="criticas"
                checked={apenasCriticas}
                onCheckedChange={(checked) => { setApenasCriticas(!!checked); setPage(1); }}
              />
              <label htmlFor="criticas" className="text-sm text-slate-600 cursor-pointer">
                Apenas críticas
              </label>
            </div>
            
            {filtrosAtivos > 0 && (
              <Button variant="outline" size="sm" onClick={clearFilters}>
                Limpar ({filtrosAtivos})
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Tabela */}
      <Card className="border-purple-100">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg">
              {loading ? 'Carregando...' : `${data.total} pendências encontradas`}
            </CardTitle>
            <Badge variant="outline" className="text-purple-600">
              {data.estatisticas?.percentual_conclusao || 0}% concluído
            </Badge>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead 
                        className="cursor-pointer hover:bg-slate-50"
                        onClick={() => handleSort('turma')}
                      >
                        <div className="flex items-center gap-1">
                          Turma
                          <ArrowUpDown className="w-3 h-3" />
                        </div>
                      </TableHead>
                      <TableHead 
                        className="cursor-pointer hover:bg-slate-50"
                        onClick={() => handleSort('disciplina')}
                      >
                        <div className="flex items-center gap-1">
                          Disciplina
                          <ArrowUpDown className="w-3 h-3" />
                        </div>
                      </TableHead>
                      <TableHead 
                        className="cursor-pointer hover:bg-slate-50"
                        onClick={() => handleSort('professor')}
                      >
                        <div className="flex items-center gap-1">
                          Professor
                          <ArrowUpDown className="w-3 h-3" />
                        </div>
                      </TableHead>
                      <TableHead>Tipo Avaliação</TableHead>
                      <TableHead className="text-center">Esperado</TableHead>
                      <TableHead className="text-center">Lançado</TableHead>
                      <TableHead 
                        className="text-center cursor-pointer hover:bg-slate-50"
                        onClick={() => handleSort('pendente')}
                      >
                        <div className="flex items-center justify-center gap-1">
                          Pendente
                          <ArrowUpDown className="w-3 h-3" />
                        </div>
                      </TableHead>
                      <TableHead 
                        className="text-center cursor-pointer hover:bg-slate-50"
                        onClick={() => handleSort('percentual')}
                      >
                        <div className="flex items-center justify-center gap-1">
                          %
                          <ArrowUpDown className="w-3 h-3" />
                        </div>
                      </TableHead>
                      <TableHead 
                        className="cursor-pointer hover:bg-slate-50"
                        onClick={() => handleSort('status')}
                      >
                        <div className="flex items-center gap-1">
                          Status
                          <ArrowUpDown className="w-3 h-3" />
                        </div>
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {data.pendencias.map((p, idx) => {
                      const statusConf = STATUS_CONFIG[p.status] || STATUS_CONFIG.pendente;
                      const StatusIcon = statusConf.icon;
                      
                      return (
                        <TableRow key={idx} className="hover:bg-purple-50/50">
                          <TableCell className="font-medium">{p.turma_nome}</TableCell>
                          <TableCell>{p.disciplina_nome}</TableCell>
                          <TableCell className="text-slate-600">{p.professor_nome}</TableCell>
                          <TableCell>
                            <Badge variant="outline" className="text-xs">{p.tipo_avaliacao}</Badge>
                          </TableCell>
                          <TableCell className="text-center">{p.total_esperado}</TableCell>
                          <TableCell className="text-center text-green-600 font-medium">{p.total_lancado}</TableCell>
                          <TableCell className="text-center text-red-600 font-bold">{p.total_pendente}</TableCell>
                          <TableCell className="text-center">
                            <span className={p.percentual_conclusao < 50 ? 'text-red-600' : p.percentual_conclusao < 80 ? 'text-yellow-600' : 'text-green-600'}>
                              {p.percentual_conclusao}%
                            </span>
                          </TableCell>
                          <TableCell>
                            <Badge className={`${statusConf.color} flex items-center gap-1 w-fit`}>
                              <StatusIcon className="w-3 h-3" />
                              {statusConf.label}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-right">
                            <Button size="sm" variant="outline" className="h-7 text-[10px]" onClick={() => verMotivo(p)} data-testid={`btn-ver-motivo-${idx}`}>
                              <Eye className="w-3 h-3 mr-1" />Ver motivo
                            </Button>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>

              {/* Paginação */}
              {data.total_pages > 1 && (
                <div className="flex items-center justify-between mt-4 pt-4 border-t">
                  <div className="text-sm text-slate-500">
                    Página {page} de {data.total_pages}
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage(p => Math.max(1, p - 1))}
                      disabled={page === 1}
                    >
                      <ChevronLeft className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage(p => Math.min(data.total_pages, p + 1))}
                      disabled={page === data.total_pages}
                    >
                      <ChevronRight className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Modal auditoria — Ver motivo */}
      {auditoria && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => setAuditoria(null)} data-testid="modal-auditoria">
          <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full p-5 max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-3">
              <div>
                <h3 className="text-lg font-bold text-purple-800">Auditoria da pendência</h3>
                <p className="text-xs text-slate-500">Detalhamento usando IDs como fonte primária.</p>
              </div>
              <button onClick={() => setAuditoria(null)} className="text-slate-400 hover:text-slate-700 text-xl">×</button>
            </div>
            {auditLoading ? (
              <div className="py-10 text-center text-slate-500"><Loader2 className="w-5 h-5 animate-spin inline" /> Auditando…</div>
            ) : (
              <div className="space-y-3 text-sm">
                <div className="rounded-md border bg-purple-50/40 p-3 text-purple-900">
                  <b>Motivo:</b> {auditoria.motivo}
                </div>
                <div className="grid grid-cols-2 gap-2 text-xs">
                  <Info label="Turma" value={auditoria.turma_nome} />
                  <Info label="Disciplina" value={auditoria.disciplina_nome} />
                  <Info label="Professor" value={auditoria.professor_nome} />
                  <Info label="Trimestre" value={auditoria.trimestre} />
                  <Info label="Tipo avaliação" value={auditoria.tipo_avaliacao} />
                  <Info label="Normalizado p/" value={auditoria.tipo_avaliacao_normalizado} />
                </div>
                <div className="grid grid-cols-4 gap-2 text-center">
                  <StatBox label="Alunos esperados" value={auditoria.totais?.alunos_esperados} color="slate" />
                  <StatBox label="Com nota" value={auditoria.totais?.alunos_com_nota} color="emerald" />
                  <StatBox label="Notas brutas" value={auditoria.totais?.notas_brutas_encontradas} color="purple" />
                  <StatBox label="Fora matrícula" value={auditoria.totais?.fora_matricula} color="amber" />
                </div>
                {auditoria.alunos_pendentes?.length > 0 && (
                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-1">Alunos pendentes ({auditoria.alunos_pendentes.length})</div>
                    <ul className="text-xs max-h-40 overflow-y-auto space-y-0.5 rounded border p-2 bg-slate-50">
                      {auditoria.alunos_pendentes.map(a => <li key={a.id}>• {a.nome}</li>)}
                    </ul>
                  </div>
                )}
                {auditoria.encontradas_fora_matricula?.length > 0 && (
                  <div className="text-[10px] text-amber-700 italic">⚠ {auditoria.encontradas_fora_matricula.length} nota(s) lançada(s) com student_id fora da matrícula desta turma.</div>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function Info({ label, value }) {
  return (
    <div className="rounded border bg-white px-2 py-1">
      <div className="text-[9px] uppercase tracking-widest text-slate-500">{label}</div>
      <div className="font-semibold text-slate-800 truncate">{value || '—'}</div>
    </div>
  );
}
function StatBox({ label, value, color }) {
  const map = { slate: 'bg-slate-50 text-slate-700', emerald: 'bg-emerald-50 text-emerald-700', purple: 'bg-purple-50 text-purple-700', amber: 'bg-amber-50 text-amber-700' };
  return (
    <div className={`rounded-md border px-2 py-1.5 ${map[color] || map.slate}`}>
      <div className="text-[9px] uppercase tracking-widest opacity-70">{label}</div>
      <div className="font-bold text-base">{value ?? 0}</div>
    </div>
  );
}
